/**
 * Grouping for the superadmin tenants list: top-level tenants are rows, and
 * their sub-tenants are extra rows revealed by expanding the parent.
 *
 * Kept pure and separate from the table because the failure mode is silent —
 * get the filtering wrong and a tenant simply never appears, with nothing on
 * screen to say so.
 */

const matches = (tenant, needle) =>
  String(tenant.name || '').toLowerCase().includes(needle) ||
  String(tenant.slug || '').toLowerCase().includes(needle);

/** Map of parent id -> children, from every tenant, sorted by name.
 *
 *  Built from the unfiltered list on purpose: searching for a parent must not
 *  strip its children off it.
 */
export const groupChildrenByParent = (tenants = []) => {
  const map = new Map();
  tenants.forEach((tenant) => {
    const parentId = tenant.parent_tenant_id;
    if (!parentId) return;
    if (!map.has(parentId)) map.set(parentId, []);
    map.get(parentId).push(tenant);
  });
  map.forEach((list) => list.sort((a, b) => String(a.name).localeCompare(String(b.name))));
  return map;
};

/**
 * Rows to render at the top level.
 *
 * A tenant qualifies when it has no parent — or its parent is missing from the
 * list entirely, in which case it is promoted rather than orphaned into
 * invisibility — and either it matches the search or one of its children does.
 */
export const selectTopLevelTenants = (tenants = [], searchTerm = '') => {
  const needle = String(searchTerm || '').trim().toLowerCase();
  const allIds = new Set(tenants.map((t) => t.id));
  const childrenByParent = groupChildrenByParent(tenants);
  const isMatch = (tenant) => !needle || matches(tenant, needle);

  return tenants
    .filter((tenant) => {
      const isTopLevel = !tenant.parent_tenant_id || !allIds.has(tenant.parent_tenant_id);
      if (!isTopLevel) return false;
      if (isMatch(tenant)) return true;
      return (childrenByParent.get(tenant.id) || []).some(isMatch);
    })
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

/**
 * Children to list under an expanded parent: all of them normally, but during
 * a search the parent itself did not match, only the ones that put it on
 * screen.
 */
export const selectVisibleChildren = (tenant, childrenByParent, searchTerm = '') => {
  const children = childrenByParent.get(tenant.id) || [];
  const needle = String(searchTerm || '').trim().toLowerCase();
  if (!needle || matches(tenant, needle)) return children;
  return children.filter((child) => matches(child, needle));
};

/** Parents that should open automatically, so a hit inside a collapsed parent
 *  is not mistaken for no result. Only parents with a matching child. */
export const parentsToAutoExpand = (tenants = [], searchTerm = '') => {
  const needle = String(searchTerm || '').trim().toLowerCase();
  if (!needle) return new Set();
  const childrenByParent = groupChildrenByParent(tenants);
  const open = new Set();
  childrenByParent.forEach((children, parentId) => {
    if (children.some((child) => matches(child, needle))) open.add(parentId);
  });
  return open;
};
